import React, { useEffect, useState } from "react";
import { Send, ShieldCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { getAthleteFourWeekOverview } from "../../lib/api";

function fmtMin(min) {
  const m = Math.round(min || 0);
  if (m <= 0) return "0";
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h ${String(m % 60).padStart(2, "0")}m` : `${m}m`;
}

function fmtKm(km) {
  const v = Number(km) || 0;
  return `${v.toFixed(v >= 10 ? 0 : 1)} km`;
}

function fmtWeek(iso) {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

export const ReportCardConfirmDialog = ({ open, onOpenChange, athlete, endWeekStart, weeksBack, onConfirm, sending }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !athlete?.id) return;
    setData(null);
    setError("");
    getAthleteFourWeekOverview(athlete.id, { end_week_start: endWeekStart, weeks_back: weeksBack })
      .then(setData)
      .catch(() => setError("Couldn't load the numbers — try again."));
  }, [open, athlete?.id, endWeekStart, weeksBack]);

  const weeks = data?.weeks || [];
  const totals = data?.totals || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="report-card-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">What {athlete?.name?.split(" ")[0]} will see</DialogTitle>
          <DialogDescription>
            These are the exact prescribed vs. actual numbers going into the email. Check them against the planner before sending.
          </DialogDescription>
        </DialogHeader>

        {error && <p className="text-sm text-[var(--tir-burgundy)]" data-testid="report-card-confirm-error">{error}</p>}
        {!data && !error && <p className="text-sm text-[var(--tir-muted)]" data-testid="report-card-confirm-loading">Loading numbers…</p>}

        {data && (
          <div className="space-y-4">
            <table className="w-full text-sm" data-testid="report-card-confirm-table">
              <thead>
                <tr className="text-[9px] tracking-[0.2em] uppercase text-[var(--tir-muted)] border-b border-[var(--tir-border)]">
                  <th className="text-left py-1.5 font-semibold">Week</th>
                  <th className="text-right py-1.5 font-semibold">Prescribed</th>
                  <th className="text-right py-1.5 font-semibold">Actual</th>
                  <th className="text-right py-1.5 font-semibold">Done</th>
                  <th className="text-right py-1.5 font-semibold">Adherence</th>
                </tr>
              </thead>
              <tbody>
                {weeks.length === 0 && (
                  <tr><td colSpan={5} className="py-3 text-center text-[var(--tir-muted)]">Nothing was prescribed in this range.</td></tr>
                )}
                {weeks.map((w) => (
                  <tr key={w.week_start} className="border-b border-[var(--tir-border)]/60" data-testid={`report-card-confirm-week-${w.week_start}`}>
                    <td className="py-2 font-mono-tir text-xs">{fmtWeek(w.week_start)}</td>
                    <td className="py-2 text-right font-mono-tir text-xs">{fmtKm(w.prescribed_km)} · {fmtMin(w.prescribed_minutes)}</td>
                    <td className="py-2 text-right font-mono-tir text-xs font-semibold">{fmtKm(w.actual_km)} · {fmtMin(w.actual_minutes)}</td>
                    <td className="py-2 text-right font-mono-tir text-xs">{w.completed}/{w.scheduled}</td>
                    <td className="py-2 text-right font-mono-tir text-xs font-semibold">{Math.round((w.adherence_pct || 0) * 100)}%</td>
                  </tr>
                ))}
              </tbody>
              {weeks.length > 0 && (
                <tfoot>
                  <tr className="text-xs font-semibold" data-testid="report-card-confirm-totals">
                    <td className="pt-2">Total</td>
                    <td className="pt-2 text-right font-mono-tir">{fmtKm(totals.prescribed_km)} · {fmtMin(totals.prescribed_minutes)}</td>
                    <td className="pt-2 text-right font-mono-tir">{fmtKm(totals.actual_km)} · {fmtMin(totals.actual_minutes)}</td>
                    <td className="pt-2 text-right font-mono-tir">{totals.completed}/{totals.scheduled}</td>
                    <td className="pt-2 text-right font-mono-tir">{Math.round((totals.adherence_pct || 0) * 100)}%</td>
                  </tr>
                </tfoot>
              )}
            </table>
            <p className="text-[10px] tracking-[0.16em] uppercase text-[var(--tir-muted)] inline-flex items-center gap-1.5">
              <ShieldCheck size={12} /> Prescribed totals use the same pace logic as your planner
            </p>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-[var(--tir-border)]">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="report-card-confirm-cancel"
            className="px-4 py-2 text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-ink)]"
          >
            Go back
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={sending || !data}
            data-testid="report-card-confirm-send"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--tir-burgundy)] hover:bg-[var(--tir-burgundy-dark)] disabled:opacity-50 text-white text-[11px] tracking-[0.22em] uppercase font-semibold"
          >
            <Send size={13} /> {sending ? "Sending…" : `Looks right — send to ${athlete?.email || ""}`}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
